import { useState, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
import POSettings from './POSettings';
import SupplierConnections from './SupplierConnections';
import BillingPage from './BillingPage';
const G=BRAND.colors.primary;

function Toggle({ on, onChange }) {
  return (
    <div onClick={()=>onChange(!on)} style={{width:40,height:22,background:on?G:'#E2E8F0',borderRadius:11,cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0}}>
      <div style={{width:18,height:18,background:'#fff',borderRadius:9,position:'absolute',top:2,left:on?20:2,transition:'left 0.2s'}}/>
    </div>
  );
}

// Stripe Connect settings. Each distributor connects THEIR OWN Stripe account;
// invoice payments go directly to them. We store only their account id here
// (the encrypted token lives server-side and is never sent to the client).
function PaymentsSettings({ dist, getToken, showToast, onSave }) {
  const [busy, setBusy] = useState(false);
  const connected = !!dist?.stripe_user_id;

  const connect = async () => {
    setBusy(true);
    try {
      const t = await getToken();
      const res = await api.withToken(t).getStripeConnectUrl();
      if (res?.ok && res.authUrl) {
        window.location.href = res.authUrl;
      } else {
        showToast?.(res?.error || 'Could not start Stripe connection', 'error');
        setBusy(false);
      }
    } catch (e) {
      showToast?.('Could not start Stripe connection: ' + e.message, 'error');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Stripe account? Clients will no longer be able to pay invoices online until you reconnect.')) return;
    setBusy(true);
    try {
      const t = await getToken();
      const res = await api.withToken(t).disconnectStripe();
      if (res?.ok) {
        showToast?.('Stripe account disconnected');
        onSave?.({ ...dist, stripe_user_id: null });
      } else {
        showToast?.(res?.error || 'Could not disconnect', 'error');
      }
    } catch (e) {
      showToast?.('Could not disconnect: ' + e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Accept invoice payments</div>
      <div style={{fontSize:13,color:'#64748B',marginBottom:24,lineHeight:1.5}}>
        Connect your own Stripe account to collect invoice payments from clients. Payments go directly to you — PromoMerge never touches your funds and takes no cut.
      </div>

      {connected ? (
        <div style={{padding:16,background:'#D1FAE5',border:'1px solid #A7F3D0',borderRadius:8,marginBottom:16}}>
          <div style={{color:'#065F46',fontWeight:600,marginBottom:4}}>✓ Stripe connected</div>
          <div style={{fontSize:12,color:'#047857'}}>Invoice payments will be sent directly to your connected Stripe account.</div>
          <button onClick={disconnect} disabled={busy}
            style={{marginTop:12,padding:'8px 16px',background:'#FEE2E2',color:'#DC2626',border:'none',borderRadius:6,cursor:busy?'not-allowed':'pointer',fontSize:13,fontWeight:500,fontFamily:'inherit',opacity:busy?0.6:1}}>
            {busy ? 'Working…' : 'Disconnect'}
          </button>
        </div>
      ) : (
        <div style={{padding:16,background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:8,marginBottom:16}}>
          <div style={{fontWeight:600,color:'#92400E',marginBottom:8}}>No Stripe account connected</div>
          <p style={{fontSize:13,color:'#92400E',marginBottom:12,lineHeight:1.5}}>
            Until you connect Stripe, invoices can be created and shared but clients won't be able to pay online.
          </p>
          <button onClick={connect} disabled={busy}
            style={{padding:'12px 24px',background:G,color:'#fff',border:'none',borderRadius:8,cursor:busy?'not-allowed':'pointer',fontWeight:600,fontSize:13,fontFamily:'inherit',opacity:busy?0.6:1}}>
            {busy ? 'Connecting…' : 'Connect Stripe Account'}
          </button>
        </div>
      )}
    </>
  );
}

export default function SettingsPage({ dist, getToken, showToast, onSave, navigate, initialTab }) {
  const [tab,       setTab]       = useState(initialTab || 'margins');
  // Team role, used only to hide the Billing tab from members. This is a UI
  // affordance — the real boundary is server-side (requireTeamRole on
  // /api/billing/portal and /api/billing/checkout).
  const [role,      setRole]      = useState(null);
  const [defMargin, setDefMargin] = useState(dist?.default_margin||40);
  const [showPrice, setShowPrice] = useState(dist?.show_pricing||false);
  const [showSup,   setShowSup]   = useState(dist?.show_supplier||false);
  const [saving,    setSaving]    = useState(false);

  // Resolve the caller's team role so the Billing tab can be hidden from
  // members. Failure is non-fatal — role stays null, the tab stays hidden,
  // and the rest of Settings works normally.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await getToken();
        const s = await api.withToken(t).getBillingStatus();
        if (!cancelled) setRole(s?.role || null);
      } catch { /* non-fatal: tab stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Handle the return trip from Stripe Connect. The OAuth callback redirects
  // to /?stripe=<result>#settings; on mount we read that, jump to the Payments
  // tab, show the outcome, and refresh the distributor so the connected state
  // renders without a manual reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('stripe');
    if (!result) return;
    setTab('payments');
    if (result === 'connected') {
      showToast?.('Stripe account connected');
      (async () => {
        try {
          const t = await getToken();
          const me = await api.withToken(t).getMe?.();
          if (me && onSave) onSave(me);
        } catch { /* non-fatal: user can reload */ }
      })();
    } else if (result === 'denied') {
      showToast?.('Stripe connection was cancelled', 'error');
    } else if (result === 'badstate') {
      showToast?.('Connection expired, please try again', 'error');
    } else if (result === 'error') {
      showToast?.('Could not connect Stripe, please try again', 'error');
    }
    // Clean the query string so a refresh doesn't repeat this.
    window.history.replaceState({}, '', `${window.location.pathname}#settings`);
    // eslint-disable-next-line
  }, []);

  const isBillingAdmin = role === 'owner' || role === 'admin';

  const save = async (extra={}) => {    setSaving(true);
    try {
      const t=await getToken();
      const updated=await api.withToken(t).updateMe({...dist,...extra,default_margin:defMargin,show_pricing:showPrice,show_supplier:showSup});
      onSave(updated); showToast('Settings saved');
    } catch(e){showToast('Save failed: '+e.message,'error');}
    finally{setSaving(false);}
  };

  // (openPortal removed — the Stripe portal is now opened by BillingPage,
  // which is the single billing surface.)

  const NavItem=({id,label})=>(
    <div onClick={()=>setTab(id)} style={{padding:'10px 12px',borderRadius:8,fontSize:13,cursor:'pointer',color:tab===id?'#059669':'#64748B',background:tab===id?`${G}08`:'transparent',fontWeight:tab===id?500:400,transition:'all 0.15s'}}>
      {label}
    </div>
  );

  return (
    <div style={{padding:24}}>
      <div style={{display:'grid',gridTemplateColumns:'200px 1fr',gap:20}}>
        <div style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:8,height:'fit-content'}}>
          <NavItem id="margins"  label="Pricing & Margins"/>
          <NavItem id="display"  label="Display Settings"/>
          <NavItem id="account"  label="Account"/>
          <NavItem id="po"       label="Purchase Orders"/>
          <NavItem id="suppliers" label="Supplier Connections"/>
          <NavItem id="notifications" label="Notifications"/>
          {isBillingAdmin && <NavItem id="payments" label="Payments"/>}
          {isBillingAdmin && <NavItem id="billing"  label="Billing"/>}
          {/* Data Quality is a PromoSearch-internal ops tool. The server-side
              endpoints reject anyone whose distributors.platform_admin != true
              with a 404. Hiding the nav item here is purely cosmetic / less
              fingerprinting — the security boundary is server-side. */}
          {dist?.platform_admin === true && <NavItem id="dataquality" label="Data Quality"/>}
        </div>
        <div style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:24}}>
          {tab==='margins'&&<>
            <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Pricing & Margins</div>
            <div style={{fontSize:13,color:'#64748B',marginBottom:24}}>Set the default margin applied to new quote lines. Override per item in the presentation builder.</div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:6,display:'block'}}>Global default margin</label>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <input type="number" min="1" max="80" value={defMargin} onChange={e=>setDefMargin(+e.target.value)}
                  style={{width:80,border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'inherit',outline:'none',textAlign:'center',color:'#0F172A'}}/>
                <span style={{fontSize:13,color:'#64748B'}}>%</span>
                <span style={{fontSize:12,color:'#94A3B8'}}>Example: $10 net → ${(10/(1-defMargin/100)).toFixed(2)}</span>
              </div>
            </div>
            {/* The per-category margin table was removed. It only ever fed the
                AI Bundle Builder — the Presentations quoting page always read
                default_margin and ignored categories entirely — so setting a
                category margin appeared to affect quotes but didn't. One
                number now means one number everywhere.

                The distributors.category_margins column is intentionally left
                in place (data preserved, nothing reads it) in case per-
                category margins come back as a real, fully-wired feature. */}
            <button onClick={()=>save()} disabled={saving} style={{background:G,border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>{saving?'Saving...':'Save Margins'}</button>
          </>}

          {tab==='display'&&<>
            <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Display Settings</div>
            <div style={{fontSize:13,color:'#64748B',marginBottom:24}}>Control what your clients see. All defaults protect your margins and supplier relationships.</div>
            {[
              {label:'Show pricing to clients',desc:'Show product prices on your storefront and presentations',val:showPrice,setter:setShowPrice},
              {label:'Show supplier names to clients',desc:'Reveal which supplier makes each product',val:showSup,setter:setShowSup},
            ].map(s=>(
              <div key={s.label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 0',borderBottom:'1px solid #F1F5F9'}}>
                <div><div style={{fontSize:13,fontWeight:500,color:'#0F172A'}}>{s.label}</div><div style={{fontSize:12,color:'#94A3B8',marginTop:2}}>{s.desc}</div></div>
                <Toggle on={s.val} onChange={s.setter}/>
              </div>
            ))}
            <div style={{marginTop:16,padding:12,background:'#F8FAFC',borderRadius:8,fontSize:12,color:'#64748B'}}>
              These defaults protect you. Turn on only if your business model requires it.
            </div>
            <button onClick={()=>save()} disabled={saving} style={{marginTop:16,background:G,border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>{saving?'Saving...':'Save Settings'}</button>
          </>}

          {tab==='account'&&<>
            <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Account Details</div>
            <div style={{fontSize:13,color:'#64748B',marginBottom:24}}>Your distributor profile.</div>
            {[{label:'Company name',key:'company',val:dist?.company||''},{label:'Your name',key:'name',val:dist?.name||''},{label:'Email',key:'email',val:dist?.email||''}].map(f=>(
              <div key={f.key} style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:5,display:'block'}}>{f.label}</label>
                <input defaultValue={f.val} style={{width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A'}}/>
              </div>
            ))}
            <button onClick={()=>save()} disabled={saving} style={{background:G,border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>{saving?'Saving...':'Save Changes'}</button>
          </>}

          {tab==='po'&&<POSettings getToken={getToken} showToast={showToast} />}
          {tab==='suppliers'&&<SupplierConnections dist={dist} getToken={getToken} showToast={showToast} />}
          {tab==='notifications'&&<EmailNotificationsSettings getToken={getToken} showToast={showToast} />}

          {tab==='payments' && isBillingAdmin && (
            <PaymentsSettings dist={dist} getToken={getToken} showToast={showToast} onSave={onSave} />
          )}

          {/* The real billing surface. This used to be a hardcoded block that
              rendered BRAND.pricing.monthly ($49) and a static feature list —
              which showed the wrong price to anyone on Growth or Enterprise.
              BillingPage reads /api/billing/status, so plan, price, trial
              state, usage, and seats are all live. */}
          {tab==='billing' && isBillingAdmin && (
            <>
              <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Billing &amp; plan</div>
              <div style={{fontSize:13,color:'#64748B',marginBottom:20}}>Manage your subscription, payment methods, and usage.</div>
              <BillingPage getToken={getToken} showToast={showToast} navigate={navigate} embedded />
            </>
          )}

          {tab==='dataquality' && dist?.platform_admin === true && <DataQualityTab getToken={getToken} showToast={showToast}/>}
        </div>
      </div>
    </div>
  );
}

// ─── Data Quality tab ──────────────────────────────────────────────────────
// Surfaces the completeness reconciliation system to admins. Three actions:
//   - View current state (active/inactive counts per supplier, inactive reasons)
//   - Preview what reconciliation WOULD do (dry-run, no writes)
//   - Run reconciliation for real (subject to COMPLETENESS_DRY_RUN_ONLY env var)
//
// The competitive-advantage rule (May 2026): a product is shown only if it
// has name + category + description + ≥1 image + ≥1 pricing tier + verified
// inventory status (in_stock or out_of_stock). Anything failing → hidden.
function DataQualityTab({ getToken, showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const t = await getToken();
      const res = await fetch('/api/admin/data-quality', { headers: { Authorization: 'Bearer ' + t } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      showToast('Failed to load data quality: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  const runPreview = async () => {
    setPreview(null);
    setRunning(true);
    try {
      const t = await getToken();
      const res = await fetch('/api/admin/data-quality/preview', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization: 'Bearer ' + t },
        body: JSON.stringify({})
      });
      const json = await res.json();
      setPreview(json);
    } catch (e) {
      showToast('Preview failed: ' + e.message, 'error');
    } finally {
      setRunning(false);
    }
  };

  const runReal = async () => {
    if (!preview) {
      showToast('Run a preview first to see the impact', 'error');
      return;
    }
    const total = (preview.wouldDeactivateCount || 0) + (preview.wouldReactivateCount || 0);
    if (total === 0) {
      showToast('Nothing to do — catalog already reconciled', 'success');
      return;
    }
    if (!window.confirm(`This will deactivate ${preview.wouldDeactivateCount} products and reactivate ${preview.wouldReactivateCount}. Continue?`)) return;
    setRunning(true);
    try {
      const t = await getToken();
      const res = await fetch('/api/admin/data-quality/run', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization: 'Bearer ' + t },
        body: JSON.stringify({})
      });
      const json = await res.json();
      if (json.forcedDryRun) {
        showToast(`Run blocked by COMPLETENESS_DRY_RUN_ONLY env var (would have changed ${total} rows)`, 'error');
      } else {
        showToast(`Reconciliation complete: ${json.deactivated} deactivated, ${json.reactivated} reactivated`, 'success');
      }
      setPreview(null);
      loadStatus();
    } catch (e) {
      showToast('Run failed: ' + e.message, 'error');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !data) {
    return <div style={{fontSize:13,color:'#64748B'}}>Loading data quality status…</div>;
  }

  return (
    <>
      <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Data Quality</div>
      <div style={{fontSize:13,color:'#64748B',marginBottom:16}}>
        Products must have name, category, description, images, pricing, AND verified inventory to be shown. Anything incomplete is hidden until the supplier provides the missing data.
      </div>

      {data?.forcedDryRun && (
        <div style={{padding:12,background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:8,fontSize:13,color:'#92400E',marginBottom:16}}>
          <strong>Preview mode active.</strong> COMPLETENESS_DRY_RUN_ONLY is set in Railway — reconciliation runs but no writes happen. Unset (or set to false) to enable real writes.
        </div>
      )}

      {data?.lastRun && (
        <div style={{padding:12,background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,fontSize:12,color:'#475569',marginBottom:16}}>
          <strong>Last automated run:</strong>{' '}
          {data.lastRun.finishedAt
            ? `${new Date(data.lastRun.finishedAt).toLocaleString()} — ${data.lastRun.ok ? '✅' : '⚠️'} ${data.lastRun.details?.deactivated ?? 0} deactivated, ${data.lastRun.details?.reactivated ?? 0} reactivated`
            : 'Never run automatically yet (scheduled Tuesday midnight PT)'}
        </div>
      )}

      <div style={{marginBottom:16,fontSize:13,fontWeight:500,color:'#374151'}}>By supplier</div>
      <table style={{width:'100%',borderCollapse:'collapse',marginBottom:24,fontSize:13}}>
        <thead>
          <tr>
            {['Supplier','Active','Hidden (completeness)','Total hidden'].map(h =>
              <th key={h} style={{fontSize:11,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'0.5px',padding:'8px 12px',textAlign:'left',borderBottom:'1px solid #E2E8F0'}}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {data?.perSupplier?.map(s => (
            <tr key={s.supplier_code}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #F1F5F9',color:'#0F172A'}}>{s.supplier_name || s.supplier_code}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #F1F5F9',color:'#0F172A',fontWeight:500}}>{s.active_count}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #F1F5F9',color:s.inactive_by_completeness > 0 ? '#B45309' : '#94A3B8'}}>{s.inactive_by_completeness}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #F1F5F9',color:'#94A3B8'}}>{s.inactive_count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {data?.reasonRollup?.length > 0 && (
        <>
          <div style={{marginBottom:8,fontSize:13,fontWeight:500,color:'#374151'}}>Hidden — by reason</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:24}}>
            {data.reasonRollup.map(r => (
              <div key={r.inactive_reason} style={{padding:'6px 12px',background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:6,fontSize:12,color:'#92400E'}}>
                {r.inactive_reason}: <strong>{r.n}</strong>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{display:'flex',gap:8,marginBottom:16}}>
        <button onClick={runPreview} disabled={running} style={{background:'#fff',border:`1px solid ${G}`,color:G,borderRadius:8,padding:'9px 16px',fontSize:13,fontWeight:500,cursor:running?'wait':'pointer',fontFamily:'inherit'}}>
          {running ? 'Running…' : 'Run preview (no writes)'}
        </button>
        <button onClick={runReal} disabled={running || !preview} style={{background:preview?G:'#E2E8F0',border:'none',color:preview?'#fff':'#94A3B8',borderRadius:8,padding:'9px 16px',fontSize:13,fontWeight:500,cursor:(running||!preview)?'not-allowed':'pointer',fontFamily:'inherit'}}>
          {running ? 'Running…' : 'Reconcile now'}
        </button>
        <button onClick={loadStatus} style={{background:'transparent',border:'1px solid #E2E8F0',color:'#64748B',borderRadius:8,padding:'9px 16px',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
          Refresh
        </button>
      </div>

      {preview && (
        <div style={{padding:16,background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,fontSize:13}}>
          <div style={{fontWeight:500,color:'#0F172A',marginBottom:8}}>Preview result</div>
          <div style={{color:'#475569',marginBottom:8}}>
            Would deactivate <strong style={{color:'#B45309'}}>{preview.wouldDeactivateCount}</strong> products and reactivate <strong style={{color:G}}>{preview.wouldReactivateCount}</strong>.
          </div>
          {preview.summary?.byMissingField && Object.keys(preview.summary.byMissingField).length > 0 && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:12,color:'#64748B',marginBottom:6}}>Missing-field breakdown (one product can be missing multiple):</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {Object.entries(preview.summary.byMissingField).map(([field, count]) => (
                  <span key={field} style={{padding:'4px 8px',background:'#fff',border:'1px solid #E2E8F0',borderRadius:6,fontSize:11,color:'#475569'}}>
                    {field}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div style={{marginTop:12,fontSize:11,color:'#94A3B8'}}>Elapsed: {preview.elapsedMs}ms</div>
        </div>
      )}
    </>
  );
}

// ─── Email notifications — connect your own SendGrid ───────────────────────
// Lets a distributor connect their own SendGrid account so client-decision
// notifications (approve / request-changes) send from their own sender
// identity instead of the platform's shared address. Same connect/test/
// disconnect shape as Supplier Connections — SendGrid exposes a standard
// SMTP relay (host smtp.sendgrid.net, username literally "apikey"), so the
// only thing a distributor needs to give us is their API key + a verified
// sender address.
function EmailNotificationsSettings({ getToken, showToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // Verified-sender lookup. The From address must exactly match an identity
  // verified in SendGrid, so we fetch the list and let them pick rather than
  // type. `senderMode` degrades to 'manual' when the key lacks the Sender
  // Authentication read scope — setup must never be blocked on a permission
  // the actual send path doesn't need.
  const [senders, setSenders]         = useState([]);
  const [senderDomains, setDomains]   = useState([]);
  const [senderMode, setSenderMode]   = useState('idle'); // idle | loading | list | manual
  const [senderNote, setSenderNote]   = useState('');
  const [customFrom, setCustomFrom]   = useState(false);

  const fetchSenders = async (keyOverride) => {
    const key = (keyOverride ?? apiKey).trim();
    setSenderMode('loading');
    setSenderNote('');
    try {
      const t = await getToken();
      const r = await api.withToken(t).listEmailSenders(key ? { api_key: key } : {});
      if (r.ok && (r.senders?.length || r.domains?.length)) {
        setSenders(r.senders || []);
        setDomains(r.domains || []);
        setSenderMode('list');
        // Auto-select when there's exactly one choice and nothing set yet —
        // the overwhelmingly common case, and the one people typo.
        if (!fromEmail && (r.senders || []).length === 1) setFromEmail(r.senders[0].email);
        if (!(r.senders || []).length) setCustomFrom(true);
      } else if (r.ok) {
        setSenderMode('manual');
        setSenderNote('No verified senders found in this SendGrid account. Verify a sender there first (Settings → Sender Authentication), then refresh.');
      } else {
        setSenderMode('manual');
        setSenderNote(r.error || 'Could not read senders from SendGrid.');
      }
    } catch (e) {
      setSenderMode('manual');
      setSenderNote('Could not reach SendGrid: ' + e.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const t = await getToken();
      const s = await api.withToken(t).getEmailCredentials();
      setStatus(s);
      setFromEmail(s.from_email || '');
      setFromName(s.from_name || '');
      setEditing(!s.connected);
    } catch (e) {
      showToast?.('Failed to load: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!apiKey.trim() || !fromEmail.trim()) { showToast?.('API key and From email are required', 'error'); return; }
    setSaving(true);
    try {
      const t = await getToken();
      await api.withToken(t).saveEmailCredentials({
        provider: 'sendgrid', api_key: apiKey.trim(), from_email: fromEmail.trim(), from_name: fromName.trim() || null,
      });
      showToast?.('SendGrid connected');
      setApiKey('');
      setEditing(false);
      load();
    } catch (e) {
      showToast?.('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect SendGrid? Notifications will fall back to the platform default until you reconnect.')) return;
    try {
      const t = await getToken();
      await api.withToken(t).deleteEmailCredentials();
      showToast?.('Disconnected');
      load();
    } catch (e) {
      showToast?.('Disconnect failed: ' + e.message, 'error');
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const t = await getToken();
      const r = await api.withToken(t).testEmailCredentials();
      if (r.ok) showToast?.('Test email sent — check your inbox');
      else showToast?.('Test failed: ' + (r.error || 'unknown error'), 'error');
    } catch (e) {
      showToast?.('Test failed: ' + e.message, 'error');
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = { width:'100%', border:'1px solid #E2E8F0', borderRadius:8, padding:'9px 11px', fontSize:13, fontFamily:'inherit', outline:'none', color:'#0F172A', boxSizing:'border-box' };

  if (loading) return <div style={{fontSize:13,color:'#64748B'}}>Loading…</div>;

  return (
    <>
      <div style={{fontSize:16,fontWeight:600,color:'#0F172A',marginBottom:4}}>Email notifications</div>
      <div style={{fontSize:13,color:'#64748B',marginBottom:20}}>
        Connect your own SendGrid account to send email from your own address. This is required to
        email purchase orders to suppliers and decorators — those go out under your name, not ours,
        so we don't send them from a shared address.
      </div>

      {status?.connected && !editing ? (
        <div style={{border:'1px solid #E2E8F0',borderRadius:12,padding:18,maxWidth:480}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <span style={{background:`${G}18`,color:'#059669',fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:10}}>Connected</span>
            <span style={{fontSize:13,fontWeight:600,color:'#0F172A'}}>SendGrid</span>
          </div>
          <div style={{fontSize:13,color:'#475569',marginBottom:4}}>
            Sending as: <strong>{status.from_name ? `${status.from_name} <${status.from_email}>` : status.from_email}</strong>
          </div>
          <div style={{display:'flex',gap:8,marginTop:14}}>
            <button onClick={test} disabled={testing}
              style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 16px',fontSize:13,color:'#475569',cursor:'pointer',fontFamily:'inherit'}}>
              {testing ? 'Sending…' : 'Send test email'}
            </button>
            <button onClick={()=>setEditing(true)}
              style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'8px 16px',fontSize:13,color:'#475569',cursor:'pointer',fontFamily:'inherit'}}>
              Update
            </button>
            <button onClick={disconnect}
              style={{background:'#fff',border:'1px solid #FCA5A5',borderRadius:8,padding:'8px 16px',fontSize:13,color:'#B91C1C',cursor:'pointer',fontFamily:'inherit'}}>
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div style={{border:'1px solid #E2E8F0',borderRadius:12,padding:18,maxWidth:480}}>
          <div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4}}>SendGrid API key</label>
            <input type="password" value={apiKey}
              onChange={e=>{
                const v = e.target.value;
                setApiKey(v);
                // Look up senders as soon as a plausibly complete key is in.
                // SendGrid keys are SG.<22 chars>.<43 chars>; the length test
                // avoids firing a request on every keystroke while typing.
                if (v.trim().startsWith('SG.') && v.trim().length > 50 && senderMode === 'idle') fetchSenders(v);
              }}
              onBlur={()=>{ if (apiKey.trim() && senderMode === 'idle') fetchSenders(); }}
              placeholder="SG.xxxxxxxx" style={inputStyle}/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4}}>From email</label>

            {senderMode === 'idle' && (
              <>
                <input value={fromEmail} onChange={e=>setFromEmail(e.target.value)}
                  placeholder="Paste your API key above first" style={{...inputStyle,background:'#F8FAFC'}} disabled/>
                <div style={{fontSize:11,color:'#94A3B8',marginTop:3}}>
                  Once your key is in, we'll load the senders you've verified in SendGrid so you can pick one.
                </div>
              </>
            )}

            {senderMode === 'loading' && (
              <div style={{...inputStyle,color:'#94A3B8',background:'#F8FAFC'}}>Loading verified senders…</div>
            )}

            {senderMode === 'list' && !customFrom && (
              <>
                <select value={fromEmail} onChange={e=>{
                    if (e.target.value === '__custom__') { setCustomFrom(true); setFromEmail(''); }
                    else {
                      setFromEmail(e.target.value);
                      // Prefill From name from the verified sender when blank.
                      const s = senders.find(x => x.email === e.target.value);
                      if (s?.name && !fromName) setFromName(s.name);
                    }
                  }} style={{...inputStyle,cursor:'pointer'}}>
                  <option value="">Select a verified sender…</option>
                  {senders.map(s => (
                    <option key={s.email} value={s.email}>{s.name ? `${s.name} <${s.email}>` : s.email}</option>
                  ))}
                  {senderDomains.length > 0 && <option value="__custom__">Another address at {senderDomains.join(' / ')}…</option>}
                </select>
                <div style={{fontSize:11,color:'#94A3B8',marginTop:3,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                  <span>Loaded from your SendGrid account.</span>
                  <button type="button" onClick={()=>fetchSenders()}
                    style={{background:'none',border:'none',padding:0,color:G,fontSize:11,cursor:'pointer',fontFamily:'inherit',textDecoration:'underline'}}>
                    Refresh
                  </button>
                </div>
              </>
            )}

            {senderMode === 'list' && customFrom && (
              <>
                <input value={fromEmail} onChange={e=>setFromEmail(e.target.value)}
                  placeholder={senderDomains.length ? `anything@${senderDomains[0]}` : 'you@yourcompany.com'} style={inputStyle}/>
                <div style={{fontSize:11,color:'#94A3B8',marginTop:3}}>
                  {senderDomains.length > 0
                    ? `You've authenticated ${senderDomains.join(', ')}, so any address at ${senderDomains.length > 1 ? 'those domains' : 'that domain'} will work.`
                    : 'Must exactly match a verified sender in SendGrid.'}
                  {senders.length > 0 && (
                    <> <button type="button" onClick={()=>{setCustomFrom(false);setFromEmail('');}}
                      style={{background:'none',border:'none',padding:0,color:G,fontSize:11,cursor:'pointer',fontFamily:'inherit',textDecoration:'underline'}}>
                      Pick from the list instead
                    </button></>
                  )}
                </div>
                {senderDomains.length > 0 && fromEmail.includes('@') &&
                 !senderDomains.some(d => fromEmail.trim().toLowerCase().endsWith('@' + d.toLowerCase())) && (
                  <div style={{fontSize:11,color:'#B45309',marginTop:4}}>
                    ⚠ That address isn't at {senderDomains.join(' or ')} — SendGrid will reject it unless it's separately verified.
                  </div>
                )}
              </>
            )}

            {senderMode === 'manual' && (
              <>
                <input value={fromEmail} onChange={e=>setFromEmail(e.target.value)}
                  placeholder="notifications@yourcompany.com" style={inputStyle}/>
                <div style={{fontSize:11,color:'#B45309',marginTop:4}}>{senderNote}</div>
                <button type="button" onClick={()=>fetchSenders()}
                  style={{background:'none',border:'none',padding:0,marginTop:4,color:G,fontSize:11,cursor:'pointer',fontFamily:'inherit',textDecoration:'underline'}}>
                  Try loading senders again
                </button>
              </>
            )}
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4}}>From name (optional)</label>
            <input value={fromName} onChange={e=>setFromName(e.target.value)} placeholder="Your Company Name" style={inputStyle}/>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={save} disabled={saving}
              style={{background:G,border:'none',borderRadius:8,padding:'9px 18px',fontSize:13,color:'#fff',fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
              {saving ? 'Saving…' : 'Save & connect'}
            </button>
            {status?.connected && (
              <button onClick={()=>setEditing(false)}
                style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 18px',fontSize:13,color:'#475569',cursor:'pointer',fontFamily:'inherit'}}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Only shown when nothing is connected. The previous copy said
          "nothing breaks" — which was wrong: emailing a PO to a supplier is
          hard-disabled without a connected account (see PODetail.jsx, and the
          server returns a 'No email account is connected' 502). The alerts
          that DO still work are the ones addressed to the distributor's own
          inbox, plus team invites — those are PromoSearch-to-user emails, so
          sending them from our address is correct. */}
      {!status?.connected && (
        <div style={{fontSize:12,color:'#64748B',marginTop:16,maxWidth:480,border:'1px solid #E2E8F0',borderRadius:10,padding:14,background:'#F8FAFC'}}>
          <div style={{fontWeight:600,color:'#0F172A',marginBottom:8}}>Until you connect an account</div>
          <div style={{marginBottom:6}}>
            <span style={{color:'#DC2626',fontWeight:600}}>Disabled ·</span> Emailing purchase orders
            to suppliers and decorators. You can still download the PDF and send it from your own
            email client.
          </div>
          <div>
            <span style={{color:'#059669',fontWeight:600}}>Still works ·</span> Alerts to your own
            inbox when a client approves or requests changes on a presentation, and team invites.
            These come from PromoMerge's address.
          </div>
          <div style={{marginTop:10,color:'#94A3B8'}}>
            <strong style={{color:'#475569'}}>Setting up SendGrid (about two minutes):</strong>
            <div style={{marginTop:5}}>1. In SendGrid, go to <em>Settings → Sender Authentication</em> and verify the address you'll send from (Single Sender Verification is the quick option; authenticating your whole domain is better for deliverability).</div>
            <div style={{marginTop:3}}>2. Go to <em>Settings → API Keys → Create API Key</em> and choose <em>Restricted Access</em>.</div>
            <div style={{marginTop:3}}>3. Set everything to <em>No Access</em> except <strong style={{color:'#475569'}}>Mail Send → Full Access</strong> and <strong style={{color:'#475569'}}>Sender Authentication → Read Access</strong>. Mail Send is what actually sends; Sender Authentication (read-only) is only so we can show you your verified addresses in a list instead of making you type one.</div>
            <div style={{marginTop:3}}>4. Copy the key — SendGrid shows it once — and paste it above, then pick your From address and send yourself a test.</div>
          </div>
        </div>
      )}
    </>
  );
}
